// src/services/aiservice.js (Frontend)
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

class AIService {
  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      timeout: 60000, // 60 seconds timeout for AI requests
      headers: {
        'Content-Type': 'application/json',
      }
    });

    // Add auth token if available
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );
  }

  // Generate trip itinerary
  async generateItinerary(data) {
    try {
      const response = await this.api.post('/ai/itinerary', data);
      return response.data;
    } catch (error) {
      this.handleError(error, 'generate itinerary');
      throw error;
    }
  }

  // Get destination recommendations
  async getDestinationRecommendations(data) {
    try {
      const response = await this.api.post('/ai/recommendations', data);
      return response.data;
    } catch (error) {
      this.handleError(error, 'get destination recommendations');
      throw error;
    }
  }

  // Get weather-based destinations
  async getWeatherBasedDestinations(data) {
    try {
      const response = await this.api.post('/ai/weather-destinations', data);
      return response.data;
    } catch (error) {
      this.handleError(error, 'get weather-based destinations');
      throw error;
    }
  }

  // Get current weather
  async getCurrentWeather(location) {
    try {
      const response = await this.api.get(`/ai/weather/${encodeURIComponent(location)}`);
      return response.data;
    } catch (error) {
      this.handleError(error, 'get current weather');
      throw error;
    }
  }

  // Get weather forecast
  async getWeatherForecast(location, days = 5) {
    try {
      const response = await this.api.get(`/ai/forecast/${encodeURIComponent(location)}`, {
        params: { days }
      });
      return response.data;
    } catch (error) {
      this.handleError(error, 'get weather forecast');
      throw error;
    }
  }

  // Health check
  async checkHealth() {
    try {
      const response = await this.api.get('/ai/health');
      return response.data;
    } catch (error) {
      this.handleError(error, 'health check');
      throw error;
    }
  }

  // Error handler
  handleError(error, operation) {
    if (error.response) {
      console.error(`${operation} failed:`, error.response.data);
      throw new Error(error.response.data.error || `Failed to ${operation}`);
    } else if (error.request) {
      console.error(`${operation} failed: No response received`);
      throw new Error('Network error. Please check your connection.');
    } else {
      console.error(`${operation} failed:`, error.message);
      throw error;
    }
  }
}

export default new AIService();